/*
  Harit Zaveri header search.

  Extends Dawn's PredictiveSearch (assets/predictive-search.js) rather than
  replacing it, so the upstream debounce, request supersession, keyboard
  traversal, aria wiring and live-region announcements keep working and future
  Dawn merges stay clean.

  Adds, per the Figma design:
    - the collapsed -> expanded search pill
    - an idle panel (Browse Jewellery / Recent Searches / Suggested Products)
      shown on focus with an empty query, where Dawn shows nothing
    - recent searches persisted in localStorage
    - a full-screen takeover on every breakpoint (Figma "Search - Default
      state"/"Search - Active State" for desktop, the "MB" frames for mobile),
      with body scroll lock and close-on-backdrop-click
    - a rotating placeholder
    - Escape-to-close (Dawn delegates that to details-modal, which this design
      does not use)
    - "View all results" wired to the real /search URL

  Requires predictive-search.js (defines PredictiveSearch) to load first.
*/

const HZ_RECENT_SEARCHES_KEY = 'hz:recent-searches';
const HZ_RECENT_SEARCHES_MAX = 5;
const HZ_PLACEHOLDER_INTERVAL = 3000;

/** Reads recent searches, tolerating disabled or corrupt storage. */
function hzGetRecentSearches() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(HZ_RECENT_SEARCHES_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((term) => typeof term === 'string') : [];
  } catch (error) {
    return [];
  }
}

function hzSetRecentSearches(terms) {
  try {
    window.localStorage.setItem(HZ_RECENT_SEARCHES_KEY, JSON.stringify(terms));
  } catch (error) {
    // Private mode or storage disabled — recent searches are a convenience only.
  }
}

class HeaderPredictiveSearch extends PredictiveSearch {
  constructor() {
    super();

    this.dropdown = this.querySelector('[data-search-dropdown]');
    this.form = this.querySelector('form');
    this.trigger = this.querySelector('[data-search-trigger]');
    this.placeholder = this.querySelector('[data-search-placeholder]');
    this.removeIconMarkup = this.querySelector('[data-recent-remove-icon]')?.innerHTML || '';
    // Captured before any render so the idle state can always be restored.
    this.idlePanelMarkup = this.predictiveSearchResults?.innerHTML ?? '';
    this.mobileMediaQuery = window.matchMedia('(max-width: 989px)');
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
  }

  connectedCallback() {
    // Dawn's element caches sibling instances so one fetch fills every cache.
    // It looks for `predictive-search` only, so re-run the query to include
    // this subclass — otherwise every keystroke would refetch.
    this.allPredictiveSearchInstances = document.querySelectorAll(
      'predictive-search, header-predictive-search'
    );

    // connectedCallback runs again if the element is moved in the DOM; the
    // listeners below must not stack up.
    if (this.hzInitialised) {
      this.renderRecentSearches();
      return;
    }
    this.hzInitialised = true;

    this.renderRecentSearches();
    this.startPlaceholderRotation();

    this.trigger?.addEventListener('click', this.onTriggerClick.bind(this));

    // The whole pill is the affordance, not just the input: a click on its
    // padding, the placeholder overlay or the border should open the panel
    // straight away rather than doing nothing. Focusing the input is what
    // opens it (onFocus below), so this only has to redirect the click - on
    // mousedown, so the panel is already up by the time the button is
    // released.
    this.form?.addEventListener('mousedown', this.onFieldPointerDown.bind(this));
    this.querySelectorAll('[data-search-close]').forEach((button) => {
      button.addEventListener('click', () => this.close(true));
    });

    // Recent-search rows are rendered after load, so delegate their removal.
    this.addEventListener('click', this.onDelegatedClick.bind(this));

    // Capture phase: this has to see the gesture start even if something
    // downstream stops the event propagating.
    document.addEventListener('mousedown', this.onDocumentPointerDown, true);
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('mousedown', this.onDocumentPointerDown, true);
    document.removeEventListener('click', this.onDocumentClick);
    this.stopPlaceholderRotation();
    this.unlockScroll();
  }

  get isMobile() {
    return this.mobileMediaQuery.matches;
  }

  get hasResults() {
    return this.getAttribute('results') === 'true';
  }

  /* ------------------------------------------------- opening and closing -- */

  onTriggerClick(event) {
    // With a term to search for the icon submits, which is also the no-JS
    // path. With an empty field there is nothing to submit, so it opens the
    // panel instead.
    if (this.getQuery().length > 0) return;

    event.preventDefault();
    this.open();
    this.input.focus();
  }

  onFieldPointerDown(event) {
    // Leave the reset and submit buttons to their own handlers.
    if (event.target.closest('button')) return;
    // Already open with the caret in the field: let the click place the caret
    // rather than stealing it. Note the isOpen half - dismissing the panel
    // leaves focus in the input, so without it a second click on the field
    // fired no focus event and matched this guard, and the panel could not be
    // reopened without clicking somewhere else first.
    if (this.isOpen && document.activeElement === this.input) return;

    event.preventDefault();
    this.open();
    this.input.focus();
  }

  onFocus() {
    // Dawn opens only when a query exists; the design shows an idle panel on
    // focus, so always open and fetch only when there is a term.
    this.open();
    if (this.getQuery().length > 0) super.onFocus();
  }

  onFocusOut() {
    // On mobile the takeover owns the screen and must stay until dismissed.
    if (this.isMobile) return;
    super.onFocusOut();
  }

  onDocumentPointerDown(event) {
    // Where the gesture began. Opening turns this element into a
    // full-viewport surface, so the element under the pointer at mouseup is
    // no longer the one that was there at mousedown, and the click the
    // browser then fires is dispatched on their common ancestor - this
    // element. Without knowing where the gesture started, that click reads as
    // a backdrop click and closes the panel the very gesture just opened.
    this.gestureStartedInside = this.contains(event.target) && event.target !== this;
  }

  onDocumentClick(event) {
    // A gesture that began inside never dismisses: that covers the click that
    // opens the panel, and dragging a text selection from the results out
    // over the backdrop.
    const startedInside = this.gestureStartedInside;
    this.gestureStartedInside = false;
    if (startedInside) return;

    // Once expanded, the takeover covers the whole viewport (both the desktop
    // full-page mode and the mobile one), so its own plain background is the
    // only "outside" a click can land on - Node.contains() treats an element
    // as containing itself, so that click would otherwise never register as
    // outside and the search would be impossible to dismiss by clicking away.
    if (event.target === this) {
      this.close();
      return;
    }
    if (this.contains(event.target)) return;
    this.close();
  }

  open() {
    this.classList.add('header__search--expanded');
    if (this.dropdown) this.dropdown.hidden = false;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    this.stopPlaceholderRotation();
    // A full-viewport takeover on both breakpoints now (Figma "Search - Default
    // state" is the same full-page treatment on desktop), so the body scrolls
    // nowhere else while it's open, regardless of screen width.
    this.lockScroll();
  }

  close(clearSearchTerm = false) {
    this.classList.remove('header__search--expanded');
    if (this.dropdown) this.dropdown.hidden = true;
    this.unlockScroll();
    this.startPlaceholderRotation();

    // Reuse Dawn's teardown for aria state, selection and loading flags.
    this.closeResults(clearSearchTerm);
    this.isOpen = false;

    if (clearSearchTerm) {
      this.searchTerm = '';
      this.renderIdlePanel();
    }
  }

  /* ----------------------------------------------------- query lifecycle -- */

  onChange() {
    // Deliberately does not call super.onChange(): Dawn closes the dropdown on
    // an empty query, whereas this design falls back to the idle panel.
    SearchForm.prototype.onChange.call(this);

    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Stale results must not reappear when the dropdown opens again.
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;
    this.updateViewAllLink(newSearchTerm);

    if (!this.searchTerm.length) {
      this.removeAttribute('results');
      this.removeAttribute('loading');
      this.renderIdlePanel();
      this.open();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormReset(event) {
    super.onFormReset(event);
    // Dawn's reset closes the results outright. This design keeps the panel
    // open and drops back to the idle recommendations - the same place
    // clearing the field by hand ends up, except that a native form reset
    // fires no input event, so onChange never runs and the stale results
    // stayed on screen behind an empty field.
    if (!this.shouldResetForm()) return;
    this.searchTerm = '';
    this.removeAttribute('results');
    this.removeAttribute('loading');
    this.updateViewAllLink('');
    this.renderIdlePanel();
    this.open();
  }

  onKeyup(event) {
    // Dawn's onKeyup closes on an empty query and has no Escape branch (it
    // relies on details-modal, which this design does not use).
    if (event.code === 'Escape') {
      event.preventDefault();
      this.close(true);
      this.trigger?.focus();
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  switchOption(direction) {
    // The idle panel is browsable content, not a listbox — arrow traversal
    // applies to fetched results only. This also guards `statusElement`, which
    // Dawn only assigns once a search has run.
    if (!this.hasResults) return;
    super.switchOption(direction);
  }

  getSearchResults(searchTerm) {
    this.setLoadingSkeleton();
    return super.getSearchResults(searchTerm);
  }

  renderSearchResults(resultsMarkup) {
    super.renderSearchResults(resultsMarkup);
    this.updateViewAllLink(this.getQuery());
    // Results replace the whole panel, including the Recent Searches column
    // the design keeps visible alongside them, so refill it from storage.
    this.renderRecentSearches();
  }

  /** Figma shows a skeleton rather than a spinner while results load. */
  setLoadingSkeleton() {
    if (!this.predictiveSearchResults) return;
    if (this.predictiveSearchResults.querySelector('[data-predictive-search-skeleton]')) return;

    this.predictiveSearchResults.innerHTML = `
      <div class="predictive-search-hz" data-predictive-search-skeleton>
        <div class="predictive-search-hz__top">
          <div class="predictive-search-hz__col predictive-search-hz__col--main">
            <ul class="predictive-search-hz__skeleton" role="presentation">
              <li class="predictive-search-hz__skeleton-row"></li>
              <li class="predictive-search-hz__skeleton-row"></li>
              <li class="predictive-search-hz__skeleton-row"></li>
            </ul>
          </div>
        </div>
      </div>
    `;
  }

  renderIdlePanel() {
    if (!this.predictiveSearchResults) return;
    this.predictiveSearchResults.innerHTML = this.idlePanelMarkup;
    this.predictiveSearchResults.removeAttribute('style');
    this.renderRecentSearches();
  }

  /* Both the visible "View All Results" pill and the hidden tracker carry
     [data-search-view-all]; the pill is re-rendered with each results panel,
     so re-query rather than caching a single node. */
  updateViewAllLink(query) {
    const base = this.form?.getAttribute('action') || '/search';
    const href = query
      ? `${base}?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last`
      : base;
    this.querySelectorAll('[data-search-view-all]').forEach((link) => {
      link.href = href;
    });
  }

  onFormSubmit(event) {
    const query = this.getQuery();
    if (query) this.rememberSearch(query);
    super.onFormSubmit(event);
  }

  /* ---------------------------------------------------- recent searches -- */

  rememberSearch(term) {
    const trimmed = term.trim();
    if (!trimmed) return;
    const rest = hzGetRecentSearches().filter(
      (item) => item.toLowerCase() !== trimmed.toLowerCase()
    );
    hzSetRecentSearches([trimmed, ...rest].slice(0, HZ_RECENT_SEARCHES_MAX));
  }

  renderRecentSearches() {
    const container = this.querySelector('[data-recent-searches]');
    const list = this.querySelector('[data-recent-searches-list]');
    if (!container || !list) return;

    // Capped on read as well as on write: storage written by an older build
    // (or another tab) can hold more than the panel is meant to show.
    const terms = hzGetRecentSearches().slice(0, HZ_RECENT_SEARCHES_MAX);
    const empty = this.querySelector('[data-recent-empty]');

    // The column keeps its half of the panel either way (per the design); only
    // the rows and the empty note swap.
    if (empty) empty.hidden = terms.length > 0;

    if (terms.length === 0) {
      list.innerHTML = '';
      return;
    }

    const base = this.form?.getAttribute('action') || '/search';
    const removeLabel = this.dataset.removeRecentLabel || 'Remove';

    list.innerHTML = terms
      .map((term) => {
        const safe = HeaderPredictiveSearch.escapeHtml(term);
        const href = `${base}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
        return `
          <li class="predictive-search-hz__row">
            <a href="${href}" class="predictive-search-hz__row-link">${safe}</a>
            <button
              type="button"
              class="predictive-search-hz__row-remove"
              data-recent-remove="${safe}"
              aria-label="${HeaderPredictiveSearch.escapeHtml(removeLabel)} ${safe}"
            >${this.removeIconMarkup}</button>
          </li>`;
      })
      .join('');
  }

  onDelegatedClick(event) {
    const removeButton = event.target.closest('[data-recent-remove]');
    if (!removeButton) return;

    event.preventDefault();
    const term = removeButton.dataset.recentRemove;
    hzSetRecentSearches(
      hzGetRecentSearches().filter((item) => item.toLowerCase() !== term.toLowerCase())
    );
    this.renderRecentSearches();
    this.input.focus();
  }

  static escapeHtml(value) {
    // The text-node round trip escapes &, <, > but not ", and the result is
    // used inside a double-quoted attribute (data-recent-remove="…") as well
    // as text content — a term like `18" Chain` (a plausible jewelry search)
    // would otherwise close the attribute early and corrupt the markup.
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML.replace(/"/g, '&quot;');
  }

  /* ----------------------------------------------- rotating placeholder -- */

  startPlaceholderRotation() {
    if (!this.placeholder || this.placeholderTimer) return;
    if (this.reducedMotionQuery.matches) return;

    this.placeholderTerms = this.placeholder.querySelectorAll('.header__search-placeholder-term');
    if (this.placeholderTerms.length < 2) return;

    this.placeholderIndex = 0;
    this.placeholderTimer = window.setInterval(() => {
      this.placeholderTerms[this.placeholderIndex].hidden = true;
      this.placeholderIndex = (this.placeholderIndex + 1) % this.placeholderTerms.length;
      this.placeholderTerms[this.placeholderIndex].hidden = false;
    }, HZ_PLACEHOLDER_INTERVAL);
  }

  stopPlaceholderRotation() {
    if (!this.placeholderTimer) return;
    window.clearInterval(this.placeholderTimer);
    this.placeholderTimer = null;
  }

  /* -------------------------------------------------------- scroll lock -- */

  lockScroll() {
    document.body.classList.add('overflow-hidden');
  }

  unlockScroll() {
    document.body.classList.remove('overflow-hidden');
  }
}

customElements.define('header-predictive-search', HeaderPredictiveSearch);
